/*
 * Order tracking (sections/track-order.liquid).
 * Sends { orderNumber, email } to the tracking service and shows what comes back:
 *   { found, orderNumber, orderDate, status, step (1-4, or -1 if cancelled), cancelled,
 *     fulfillments: [{ trackingCompany, trackingNumber, trackingUrl }],
 *     lineItems: [{ title, variantTitle, quantity }] }
 * (Same service and response as the live site's tracking page.)
 */
class TrackOrder extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-track-form]');
    this.button = this.querySelector('[data-track-submit]');
    this.message = this.querySelector('[data-track-message]');
    this.result = this.querySelector('[data-track-result]');
    this.form.addEventListener('submit', (event) => {
      event.preventDefault();
      this.lookUp();
    });
  }

  async lookUp() {
    const orderNumber = this.form.orderNumber.value.trim();
    const email = this.form.email.value.trim();
    this.result.hidden = true;

    if (!orderNumber || !email) {
      this.setMessage('please enter both your order number and email.', true);
      return;
    }

    this.setMessage('');
    this.setLoading(true);

    try {
      const response = await fetch(this.dataset.api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderNumber, email }),
      });
      const data = await response.json();

      if (!response.ok || !data.found) {
        this.setMessage((data.message || 'order not found.').toLowerCase(), true);
        return;
      }
      this.render(data);
    } catch {
      this.setMessage('something went wrong. please try again in a moment.', true);
    } finally {
      this.setLoading(false);
    }
  }

  render(order) {
    this.querySelector('[data-track-number]').textContent = order.orderNumber;
    this.querySelector('[data-track-date]').textContent = order.orderDate
      ? new Date(order.orderDate).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }).toLowerCase()
      : '';

    const badge = this.querySelector('[data-track-badge]');
    badge.textContent = (order.status || '').toLowerCase();
    badge.dataset.status = order.cancelled ? 'cancelled' : order.step === 4 ? 'delivered' : '';

    // Steps: earlier ones ticked off, current one highlighted, none if cancelled
    this.querySelectorAll('[data-track-steps] li').forEach((li) => {
      const step = Number(li.dataset.step);
      li.classList.toggle('is-complete', order.step > 0 && step < order.step);
      li.classList.toggle('is-current', order.step > 0 && step === order.step);
    });

    const fulfillments = this.querySelector('[data-track-fulfillments]');
    fulfillments.replaceChildren();
    (order.fulfillments || []).forEach((f) => {
      if (!f.trackingNumber) return;
      const row = document.createElement('div');
      row.className = 'track-order__fulfillment';
      row.append(`${(f.trackingCompany || 'carrier').toLowerCase()} • ${f.trackingNumber}`);
      if (f.trackingUrl) {
        const link = Object.assign(document.createElement('a'), {
          href: f.trackingUrl,
          target: '_blank',
          rel: 'noopener noreferrer',
          textContent: 'track shipment →',
        });
        row.append(link);
      }
      fulfillments.append(row);
    });

    const items = this.querySelector('[data-track-items]');
    items.replaceChildren();
    (order.lineItems || []).forEach((item) => {
      const row = document.createElement('div');
      row.className = 'track-order__item';
      const name = document.createElement('span');
      name.textContent = item.title.toLowerCase();
      if (item.variantTitle) {
        const variant = Object.assign(document.createElement('span'), {
          className: 'track-order__variant',
          textContent: item.variantTitle.toLowerCase(),
        });
        name.append(variant);
      }
      row.append(name, `qty ${item.quantity}`);
      items.append(row);
    });

    this.result.hidden = false;
  }

  setMessage(text, isError = false) {
    this.message.textContent = text;
    this.message.classList.toggle('is-error', isError);
  }

  setLoading(loading) {
    this.button.disabled = loading;
    this.button.textContent = loading ? 'looking up...' : 'track order';
  }
}

customElements.define('track-order', TrackOrder);
